import React, { useState, useEffect } from 'react'
import { plants } from '../data/plants'

interface Task {
  id: string
  plantId: string
  task: 'start-indoors' | 'direct-sow' | 'transplant' | 'harvest' | 'fertilize' | 'water' | 'weed'
  date: Date
  completed: boolean
  notes?: string
}

interface TaskListProps {
  zipCode: string | undefined
  frostDate: Date | undefined
}

const TaskList: React.FC<TaskListProps> = ({ zipCode, frostDate }) => {
  const [tasks, setTasks] = useState<Task[]>([])
  const [filter, setFilter] = useState<'all' | 'today' | 'week' | 'overdue'>('all')

  useEffect(() => {
    if (frostDate) {
      generateTasks(frostDate)
    }
  }, [frostDate])

  const generateTasks = (frostDate: Date) => {
    const year = frostDate.getFullYear()
    const newTasks: Task[] = []

    plants.slice(0, 20).forEach(plant => { // Limit to 20 plants for demo
      // Start indoors (6-8 weeks before last frost)
      if (plant.daysToGermination < 14) {
        const startIndoors = new Date(frostDate)
        startIndoors.setDate(startIndoors.getDate() - 56)
        newTasks.push({
          id: `${plant.id}-start-${year}`,
          plantId: plant.id,
          task: 'start-indoors',
          date: startIndoors,
          completed: false
        })
      }

      // Direct sow (on or after last frost)
      const directSow = new Date(frostDate)
      directSow.setDate(directSow.getDate() + (plant.daysToGermination > 10 ? 14 : 0))
      newTasks.push({
        id: `${plant.id}-sow-${year}`,
        plantId: plant.id,
        task: 'direct-sow',
        date: directSow,
        completed: false
      })

      // Harvest (after days to harvest)
      const harvest = new Date(frostDate)
      harvest.setDate(harvest.getDate() + plant.daysToHarvest)
      newTasks.push({
        id: `${plant.id}-harvest-${year}`,
        plantId: plant.id,
        task: 'harvest',
        date: harvest,
        completed: false
      })
    })

    // Add weekly maintenance tasks
    for (let week = 0; week < 20; week++) {
      const weekDate = new Date(frostDate)
      weekDate.setDate(weekDate.getDate() + (week * 7))
      
      newTasks.push({
        id: `fertilize-${week}`,
        plantId: 'general',
        task: 'fertilize',
        date: weekDate,
        completed: false,
        notes: 'Apply compost or organic fertilizer'
      })
    }

    setTasks(newTasks.sort((a, b) => a.date.getTime() - b.date.getTime()))
  }

  const toggleTask = (taskId: string) => {
    setTasks(tasks.map(task => 
      task.id === taskId ? { ...task, completed: !task.completed } : task
    ))
  }

  const getTaskLabel = (task: Task['task']) => {
    switch (task) {
      case 'start-indoors': return { text: 'Start Indoors', icon: '🌱', color: '#4caf50' }
      case 'direct-sow': return { text: 'Direct Sow', icon: '🌿', color: '#8bc34a' }
      case 'transplant': return { text: 'Transplant', icon: '🪴', color: '#795548' }
      case 'harvest': return { text: 'Harvest', icon: '🥕', color: '#ff9800' }
      case 'fertilize': return { text: 'Fertilize', icon: '💩', color: '#795548' }
      case 'water': return { text: 'Water', icon: '💧', color: '#2196f3' }
      case 'weed': return { text: 'Weed', icon: '🌾', color: '#ff5722' }
    }
  }

  const getPlantName = (plantId: string) => {
    if (plantId === 'general') return 'General Garden'
    const plant = plants.find(p => p.id === plantId)
    return plant?.name || 'Unknown Plant'
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const filteredTasks = tasks.filter(task => {
    const taskDate = new Date(task.date)
    taskDate.setHours(0, 0, 0, 0)
    
    switch (filter) {
      case 'today':
        return taskDate.getTime() === today.getTime() && !task.completed
      case 'week':
        const weekFromNow = new Date(today)
        weekFromNow.setDate(weekFromNow.getDate() + 7)
        return taskDate >= today && taskDate <= weekFromNow && !task.completed
      case 'overdue':
        return taskDate < today && !task.completed
      default:
        return !task.completed
    }
  })

  const completedCount = tasks.filter(t => t.completed).length
  const totalCount = tasks.length

  return (
    <div>
      <div className="card">
        <h2>Your Garden Tasks</h2>
        <p>Personalized task list based on your frost date and selected plants.</p>
        
        <div style={{ marginTop: '1rem', padding: '1rem', background: '#e8f5e9', borderRadius: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ fontSize: '2rem' }}>📊</div>
            <div>
              <p style={{ margin: 0, fontWeight: 500 }}>
                {completedCount} of {totalCount} tasks completed
              </p>
              <div style={{ 
                width: '200px', 
                height: '8px', 
                background: '#ddd', 
                borderRadius: '4px', 
                marginTop: '0.5rem',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${(completedCount / totalCount) * 100}%`,
                  height: '100%',
                  background: '#4caf50',
                  transition: 'width 0.3s'
                }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="card" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {(['all', 'today', 'week', 'overdue'] as const).map((f) => (
          <button
            key={f}
            className="btn"
            style={{
              background: filter === f ? '#2d5a27' : 'white',
              color: filter === f ? 'white' : '#2d5a27',
              textTransform: 'capitalize'
            }}
            onClick={() => setFilter(f)}
          >
            {f === 'all' && 'All Tasks'}
            {f === 'today' && 'Today'}
            {f === 'week' && 'This Week'}
            {f === 'overdue' && 'Overdue'}
          </button>
        ))}
      </div>

      {/* Task List */}
      <div className="card">
        {filteredTasks.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666' }}>
            {filter === 'all' 
              ? 'No pending tasks! Great job! 🎉'
              : filter === 'today'
              ? 'No tasks for today. Enjoy your garden! 🌸'
              : filter === 'week'
              ? 'No tasks this week. Time to relax! ☀️'
              : 'No overdue tasks. Staying on top of it! 💪'
            }
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {filteredTasks.map(task => {
              const taskInfo = getTaskLabel(task.task)
              const isOverdue = new Date(task.date) < today
              
              return (
                <div
                  key={task.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1rem',
                    background: isOverdue ? '#ffebee' : 'white',
                    border: `1px solid ${isOverdue ? '#f44336' : '#ddd'}`,
                    borderRadius: '8px',
                    transition: 'all 0.2s'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={task.completed}
                    onChange={() => toggleTask(task.id)}
                    style={{ width: '24px', height: '24px', cursor: 'pointer' }}
                  />
                  
                  <span style={{ fontSize: '1.5rem' }}>{taskInfo.icon}</span>
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>
                      {taskInfo.text}: {getPlantName(task.plantId)}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                      {task.date.toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                      {isOverdue && (
                        <span style={{ color: '#f44336', marginLeft: '0.5rem', fontWeight: 500 }}>
                          OVERDUE
                        </span>
                      )}
                    </div>
                    {task.notes && (
                      <div style={{ fontSize: '0.85rem', color: '#888', marginTop: '0.25rem' }}>
                        {task.notes}
                      </div>
                    )}
                  </div>
                  
                  <div 
                    style={{ 
                      width: '12px', 
                      height: '12px', 
                      borderRadius: '50%', 
                      background: taskInfo.color 
                    }} 
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Completed Tasks Toggle */}
      {completedCount > 0 && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <details>
            <summary style={{ cursor: 'pointer', fontWeight: 500 }}>
              Show {completedCount} completed tasks
            </summary>
            <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {tasks.filter(t => t.completed).map(task => {
                const taskInfo = getTaskLabel(task.task)
                return (
                  <div
                    key={task.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '0.75rem',
                      background: '#f5f5f5',
                      borderRadius: '8px',
                      opacity: 0.7
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={true}
                      onChange={() => toggleTask(task.id)}
                      style={{ width: '20px', height: '20px' }}
                    />
                    <span style={{ textDecoration: 'line-through', color: '#888' }}>
                      {taskInfo.text}: {getPlantName(task.plantId)}
                    </span>
                  </div>
                )
              })}
            </div>
          </details>
        </div>
      )}
    </div>
  )
}

export default TaskList
